import React, { useState } from 'react';
import type { AdminTransferDetail, LeaseChoice } from '@platform/shared-types';
import { businessTransferApi } from '../../../services/businessTransferApi';
import { toastService } from '../../../services/toastService';
import { money } from '../format';

type P = { detail: AdminTransferDetail; canAct: boolean; onChange: (d: AdminTransferDetail) => void };

export default function LeasePanel({ detail, canAct, onChange }: P) {
  const t = detail.transfer;
  const units = t.dryRun?.leasedUnits || [];
  const choiceOf = (unitId: string): LeaseChoice => t.options.leaseChoices.find((c) => c.unitId === unitId)?.choice || t.options.leaseDefault;
  const [busy, setBusy] = useState(false);
  const locked = !!(t.runAt || t.run);
  const setChoice = async (unitId: string, choice: LeaseChoice) => {
    setBusy(true);
    const leaseChoices = units.map((u) => ({ unitId: u.unitId, choice: u.unitId === unitId ? choice : choiceOf(u.unitId) }));
    try { onChange(await businessTransferApi.updateOptions(t.transferId, { leaseChoices })); }
    catch (e: any) { toastService.error(e.message); } finally { setBusy(false); }
  };
  if (!units.length) return <div className="text-sm text-stone-600">No leased or rented devices at this store. Every device still moves.</div>;
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="rounded-xl border border-[#DC143C] bg-rose-50/50 px-3 py-2"><span className="text-xs text-stone-500">Pay-off invoice to seller</span><b className="block font-mono text-lg">{money(detail.payoffCents)}</b></div>
        <div className="rounded-xl border border-stone-200 bg-white px-3 py-2"><span className="text-xs text-stone-500">Buyer takes over (monthly)</span><b className="block font-mono text-lg">{money(detail.takeoverMonthlyCents)}</b></div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-stone-200">
        <table className="w-full">
          <thead className="bg-stone-50"><tr>{['Device', 'Agreement', 'Paid', 'Left to pay', 'At transfer'].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{h}</th>)}</tr></thead>
          <tbody>
            {units.map((u) => {
              const c = choiceOf(u.unitId);
              const opts: Array<[LeaseChoice, string]> = u.rental ? [['payoff', 'End rental'], ['takeover', 'Buyer continues']] : [['payoff', 'Pay off'], ['takeover', 'Buyer takes over']];
              return (
                <tr key={u.unitId} className="border-t border-stone-100">
                  <td className="px-3 py-3"><b>{u.label}</b></td>
                  <td className="px-3 py-3">{u.rental ? `Rental · ${money(u.monthlyAmountCents)}/mo` : `${u.saleMode === 'lease' ? 'Lease' : 'Installments'} · ${money(u.monthlyAmountCents)}/mo · ${u.termMonths ?? '—'} mo`}</td>
                  <td className="px-3 py-3 font-mono text-xs">{u.rental ? '—' : `${u.monthsPaid} of ${u.termMonths ?? '—'}`}</td>
                  <td className="px-3 py-3 text-right font-mono">{u.rental ? '—' : money(u.remainingCents)}</td>
                  <td className="px-3 py-3">
                    <div className="inline-flex overflow-hidden rounded-lg border border-stone-300">
                      {opts.map(([value, label]) => (
                        <button key={value} type="button" disabled={!canAct || locked || busy} onClick={() => c !== value && setChoice(u.unitId, value)}
                          className={`min-h-[44px] whitespace-nowrap px-3 text-xs font-semibold ${c === value ? 'bg-[#9F1239] text-white' : 'bg-white text-stone-600'} [&+&]:border-l [&+&]:border-stone-300`}>{label}</button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {t.buyer.signature && detail.takeoverMonthlyCents > 0 && !t.buyer.leaseSignature && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-orange-900">The buyer has been sent the lease terms to sign. Scheduling waits until they do.</div>
      )}
    </div>
  );
}
